import { motion, AnimatePresence } from "framer-motion";
import { useRef } from "react";

const variants = {
  initial: {
    x: "100%",
    opacity: 0,
  },
  animate: {
    x: 0,
    opacity: 1,
  },
  exit: {
    x: "-30%",
    opacity: 0,
  },
};

const transition = {
  type: "tween",
  ease: [0.25, 0.46, 0.45, 0.94], // iOS cubic-bezier easing
  duration: 0.35,
};

export default function PageTransition({ children }) {
  const isFirstRender = useRef(true);

  return (
    <motion.div
      variants={variants}
      initial={isFirstRender.current ? false : "initial"}
      animate="animate"
      exit="exit"
      transition={transition}
      onAnimationComplete={() => {
        isFirstRender.current = false;
      }}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        minHeight: "100%",
        width: "100%",
        willChange: "transform, opacity",
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
        transform: "translateZ(0)",
        WebkitTransform: "translateZ(0)",
        overflow: "hidden auto",
      }}
    >
      {children}
    </motion.div>
  );
}

/**
 * ─── ROUTER WRAPPER USAGE ────────────────────────────────────────────────────
 *
 * Wrap your <Routes> with AnimatePresence in your App.jsx / router file:
 *
 * import { AnimatePresence } from "framer-motion";
 * import { useLocation, Routes, Route } from "react-router-dom";
 * import PageTransition from "./PageTransition";
 *
 * export default function App() {
 *   const location = useLocation();
 *
 *   return (
 *     // position: relative is required so absolute children stack correctly
 *     <div style={{ position: "relative", overflow: "hidden", minHeight: "100vh" }}>
 *       <AnimatePresence mode="popLayout" initial={false}>
 *         <Routes location={location} key={location.pathname}>
 *           <Route path="/" element={
 *             <PageTransition><Home /></PageTransition>
 *           } />
 *           <Route path="/about" element={
 *             <PageTransition><About /></PageTransition>
 *           } />
 *         </Routes>
 *       </AnimatePresence>
 *     </div>
 *   );
 * }
 *
 * ─── KEY NOTES ───────────────────────────────────────────────────────────────
 *
 *  x: "100%"               → Slides in from full right edge (native push feel)
 *  x: "-30%"               → Previous page shifts left (iOS parallax depth)
 *  ease: [0.25,0.46,0.45,0.94] → Exact iOS easeOut cubic-bezier
 *  type: "tween"           → Cleaner than spring for page navigation
 *  duration: 0.35          → Native apps use 300–400ms for page transitions
 *  position: absolute      → Pages stack during transition
 *  mode="popLayout"        → Exit + enter animations run simultaneously
 *  initial={false}         → No animation on first page load (native behavior)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */
